import { DeleteOutlined } from '@ant-design/icons'
import { ProFormGroup } from '@ant-design/pro-components'
import type { Station } from '@app/icare/db/schema/common'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import type { PreferredCommunicationType } from '@pkg/form/common'
import { FavoriteField } from '@pkg/form/components/FavoriteField'
import { useFieldContext, useFormContext } from '@pkg/form/createAppFormHook'
import { joinNames } from '@pkg/form/utils/form.utils'
import { stopEvent } from '@pkg/shared/utils/ui.utils'
import { useStore } from '@tanstack/react-form'
import { Button } from 'antd'
import { useCallback } from 'react'

export type StationFieldProps = {
  className?: string
  hideFavorite?: boolean
  onRemove?: () => void
}

export function StationField(props: StationFieldProps) {
  const { className, hideFavorite, onRemove } = props
  const field = useFieldContext<Station>()
  const form = useFormContext()

  const preferredCommunication = useStore(field.store, s => s.value?.contact?.preferredCommunication)

  const toggleFavorite = useCallback(
    (type: PreferredCommunicationType) => {
      const currentValue = field.state.value?.contact?.preferredCommunication
      const nextValue = currentValue === type ? null : type

      field.setValue(v => ({
        ...v,
        contact: {
          ...v?.contact,
          preferredCommunication: nextValue,
        },
      }))
    },
    [field],
  )

  return (
    <ProFormGroup
      title={onRemove && <Button type="text" danger icon={<DeleteOutlined />} onClick={stopEvent(onRemove)} />}
      rowProps={{ gutter: [16, 0] }}
      className={className}
    >
      <form.AppField name={joinNames(field, 'name')}>
        {field => <field.Text label={t`Name`} colProps={{ md: 12, xs: 24 }} />}
      </form.AppField>
      <form.AppField name={joinNames(field, 'specialization')}>
        {field => <field.Text label={t`Spezialisierung`} colProps={{ md: 12, xs: 24 }} />}
      </form.AppField>

      <form.AppField name={joinNames(field, 'contact.email')}>
        {field => (
          <field.Text
            label={<Trans>E-Mail</Trans>}
            colProps={{ md: 8, xs: 24 }}
            fieldProps={{
              suffix: !hideFavorite && (
                <FavoriteField type="email" value={preferredCommunication} onChange={toggleFavorite} />
              ),
            }}
          />
        )}
      </form.AppField>
      <form.AppField name={joinNames(field, 'contact.phone')}>
        {field => (
          <field.Text
            label={<Trans>Telefonnummer</Trans>}
            colProps={{ md: 8, xs: 24 }}
            fieldProps={{
              suffix: !hideFavorite && (
                <FavoriteField type="phone" value={preferredCommunication} onChange={toggleFavorite} />
              ),
            }}
          />
        )}
      </form.AppField>
      <form.AppField name={joinNames(field, 'contact.fax')}>
        {field => (
          <field.Text
            label={<Trans>Faxnummer</Trans>}
            colProps={{ md: 8, xs: 24 }}
            fieldProps={{
              suffix: !hideFavorite && (
                <FavoriteField type="fax" value={preferredCommunication} onChange={toggleFavorite} />
              ),
            }}
          />
        )}
      </form.AppField>

      <form.AppField name={joinNames(field, 'comment')}>
        {field => <field.TextArea label={t`Memo`} colProps={{ md: 24, xs: 24 }} />}
      </form.AppField>
    </ProFormGroup>
  )
}
